/* eslint-disable @typescript-eslint/no-use-before-define */
// Abstract base ↔ concrete subclass references below are intentional and safe
// under class hoisting — every reference site runs after both classes are fully
// initialized.
import { case as case_, field, union } from "@stellar/js-xdr";
import { XdrError, type XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";
import { EnvelopeType } from "./envelope-type.js";
import {
  TransactionV1Envelope,
  type TransactionV1EnvelopeWire,
} from "./transaction-v1-envelope.js";

export type FeeBumpTransactionInnerTxWire = {
  type: 2;
  v1: TransactionV1EnvelopeWire;
};

export type FeeBumpTransactionInnerTxVariantName = "envelopeTypeTx";

/**
 * ```xdr
 * union switch (EnvelopeType type)
 *     {
 *     case ENVELOPE_TYPE_TX:
 *         TransactionV1Envelope v1;
 *     }
 * ```
 */
abstract class FeeBumpTransactionInnerTxBase extends XdrValue {
  abstract readonly type: FeeBumpTransactionInnerTxVariantName;

  constructor() {
    super();
    // `new.target`, not an unconditional throw: every arm subclass reaches
    // this constructor through `super()`, void arms via an implicit one
    if (new.target === FeeBumpTransactionInnerTxBase) {
      throw new TypeError(
        "new xdr.FeeBumpTransactionInnerTx(...) is not supported: XDR unions are built from " +
          "per-variant factories. Call xdr.FeeBumpTransactionInnerTx.envelopeTypeTx(...) " +
          "(or another arm factory) instead.",
      );
    }
  }

  static readonly schema: XdrType<FeeBumpTransactionInnerTxWire> = union(
    "FeeBumpTransactionInnerTx",
    {
      switchOn: EnvelopeType.schema,
      cases: [
        case_("envelopeTypeTx", 2, field("v1", TransactionV1Envelope.schema)),
      ],
    },
  );

  static envelopeTypeTx(
    v1: TransactionV1Envelope,
  ): FeeBumpTransactionInnerTxTx {
    return new FeeBumpTransactionInnerTxTx(v1);
  }

  static fromXdrObject(
    wire: FeeBumpTransactionInnerTxWire,
  ): FeeBumpTransactionInnerTx {
    switch (wire.type) {
      case 2:
        return new FeeBumpTransactionInnerTxTx(
          TransactionV1Envelope.fromXdrObject(wire.v1),
        );
    }
    // unreachable for a well-typed wire object; a hand-built one can still
    // carry an out-of-range discriminant
    throw new XdrError(
      `FeeBumpTransactionInnerTx: unknown type ${(wire as { type: unknown }).type}`,
    );
  }

  /**
   * Type guard narrowing an unknown value to a concrete FeeBumpTransactionInnerTx variant.
   * Use this instead of `instanceof FeeBumpTransactionInnerTx`: the exported `FeeBumpTransactionInnerTx` value
   * is the abstract base, so `instanceof` narrows to the base (not the
   * variant union) and forces a cast. `FeeBumpTransactionInnerTx.is(x)` narrows to the union.
   */
  static is(value: unknown): value is FeeBumpTransactionInnerTx {
    return value instanceof FeeBumpTransactionInnerTxBase;
  }

  abstract toXdrObject(): FeeBumpTransactionInnerTxWire;
}

export class FeeBumpTransactionInnerTxTx extends FeeBumpTransactionInnerTxBase {
  readonly type = "envelopeTypeTx" as const;
  readonly v1: TransactionV1Envelope;

  constructor(v1: TransactionV1Envelope) {
    super();
    this.v1 = v1;
  }

  get value(): TransactionV1Envelope {
    return this.v1;
  }

  toXdrObject(): Extract<FeeBumpTransactionInnerTxWire, { type: 2 }> {
    return { type: 2, v1: this.v1.toXdrObject() };
  }
}

export type FeeBumpTransactionInnerTx = FeeBumpTransactionInnerTxTx;
export const FeeBumpTransactionInnerTx = FeeBumpTransactionInnerTxBase;
